import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  Fab,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { Producto } from "../models/ProductoModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { fetchCrearPedido } from "../services/pedidoService";
import { fetchGetCatalogoCliente } from "../services/productoService";
import ProductoGaleria from "../components/ProductoGaleria";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import { parseDecimalValue } from "../utils/NumberParser";
import { isAbortError } from "../services/fetchService";

const getToday = () => new Date().toISOString().slice(0, 10);
const CARRITO_STORAGE_PREFIX = "genfe_cliente_carrito";

const getProductoId = (producto: Producto) => Number((producto as any).IDPRODUCTO ?? (producto as any).IdProducto ?? (producto as any).idProducto ?? (producto as any).id_producto ?? (producto as any).ID ?? (producto as any).id ?? 0);
const getProductoKey = (producto: Producto) => {
  const id = getProductoId(producto);
  return id > 0 ? `id-${id}` : `codigo-${getProductoCodigo(producto)}`;
};
const getProductoKeys = (producto: Producto) => {
  const keys = new Set<string>();
  const id = getProductoId(producto);
  const codigo = getProductoCodigo(producto);
  if (id > 0) keys.add(`id-${id}`);
  if (codigo) keys.add(`codigo-${codigo}`);
  return Array.from(keys);
};
const getProductoNombre = (producto: Producto) => String((producto as any).PRODUCTO ?? (producto as any).producto ?? "");
const getProductoCodigo = (producto: Producto) => String((producto as any).CODIGO ?? (producto as any).codigo ?? "");
const getProductoPrecio = (producto: Producto) => parseDecimalValue((producto as any).CONTADO ?? (producto as any).contado ?? (producto as any).precio ?? (producto as any).Precio ?? 0);
const getProductoImagen = (producto: Producto) => String((producto as any).FOTO_URL ?? (producto as any).fotoUrl ?? (producto as any).imagenUrl ?? "");
const getProductoImagenLocal = (producto: Producto) => String((producto as any).imagenLocalUrl ?? "");
const getProductoIdRubro = (producto: Producto) => Number((producto as any).IDRUBRO ?? (producto as any).IdRubro ?? (producto as any).idRubro ?? 0);
const getProductoRubro = (producto: Producto) => String((producto as any).RUBRO ?? (producto as any).rubro ?? "").trim();

const normalizeProductos = (data: any): Producto[] => {
  const parsed = typeof data === "string" ? JSON.parse(data) : data;
  return Array.isArray(parsed) ? parsed : [];
};

const normalizeProductoCarrito = (producto: Producto): Producto => {
  const idProducto = getProductoId(producto);
  return {
    ...producto,
    IDPRODUCTO: idProducto,
    CODIGO: getProductoCodigo(producto),
    PRODUCTO: getProductoNombre(producto),
    CONTADO: getProductoPrecio(producto),
    IDRUBRO: getProductoIdRubro(producto),
    RUBRO: getProductoRubro(producto),
    imagenUrl: getProductoImagen(producto),
    imagenLocalUrl: getProductoImagenLocal(producto),
  };
};

const readCarritoDraft = (storageKey: string) => {
  const raw = sessionStorage.getItem(storageKey);
  if (!raw) return undefined;

  try {
    return JSON.parse(raw);
  } catch {
    sessionStorage.removeItem(storageKey);
    return undefined;
  }
};

const sanitizeLineasForStorage = (lineas: LineaVenta[]): LineaVenta[] =>
  lineas.map((linea) => {
    const { imagenLocalUrl, ...productoSinBlob } = linea.producto as any;
    return {
      ...linea,
      producto: productoSinBlob,
    };
  });

type ProductoImagenCatalogoProps = {
  producto: Producto;
  userLoggedIn: ReturnType<typeof useUserContext>["user"];
  onLoaded: (productoKey: string, imagen: string) => void;
};

const ProductoImagenCatalogo = ({ producto, userLoggedIn, onLoaded }: ProductoImagenCatalogoProps) => {
  const productoKey = getProductoKey(producto);
  const nombre = getProductoNombre(producto);
  const imagenUrl = getProductoImagen(producto);
  return <ProductoGaleria nombre={nombre} principalUrl={imagenUrl}
    galeriaUrl={`/Producto/CatalogoCliente/Galeria/${getProductoId(producto)}`}
    token={userLoggedIn?.token} height={160}
    onPrincipalLoaded={(url) => onLoaded(productoKey, url)} />;
};

const PedidoClienteCarrito = () => {
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);
  const [idLocal, setIdLocal] = useState<number>(0);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [imagenesProductos, setImagenesProductos] = useState<Record<string, string>>({});
  const [lineas, setLineas] = useState<LineaVenta[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [idRubroSeleccionado, setIdRubroSeleccionado] = useState<number>(0);
  const [fechaEntrega, setFechaEntrega] = useState(getToday());
  const [horaDesde, setHoraDesde] = useState("09:00");
  const [horaHasta, setHoraHasta] = useState("18:00");
  const [retiraDelLocal, setRetiraDelLocal] = useState(true);
  const [sucursalEntrega, setSucursalEntrega] = useState("");
  const [observacion, setObservacion] = useState("");
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const idCliente = Number(userLoggedIn?.idCliente ?? 0);
  const cliente = userLoggedIn?.cliente ?? userLoggedIn?.username ?? "";
  const carritoStorageKey = idCliente && idLocal ? `${CARRITO_STORAGE_PREFIX}_${idCliente}_${idLocal}` : "";

  useEffect(() => {
    if (!userLoggedIn) {
      setProductos([]);
      return;
    }

    const controller = new AbortController();
    setLoadingProductos(true);
    fetchGetCatalogoCliente(userLoggedIn, controller.signal)
      .then((data) => {
        setIdLocal(data.idLocal);
        setProductos(normalizeProductos(data.productos));
        setIdRubroSeleccionado(0);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.log(err);
        showNotification("No se pudieron cargar los productos", "error");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingProductos(false);
      });

    return () => controller.abort();
  }, [userLoggedIn, showNotification]);

  const registrarImagenProducto = useCallback((productoKey: string, imagen: string) => {
    setImagenesProductos((actuales) => actuales[productoKey] ? actuales : { ...actuales, [productoKey]: imagen });
  }, []);

  useEffect(() => {
    if (!carritoStorageKey) return;

    const draft = readCarritoDraft(carritoStorageKey);
    if (!draft) return;

    setLineas(Array.isArray(draft.lineas) ? draft.lineas : []);
    setFechaEntrega(draft.fechaEntrega || getToday());
    setHoraDesde(draft.horaDesde || "09:00");
    setHoraHasta(draft.horaHasta || "18:00");
    setRetiraDelLocal(draft.retiraDelLocal ?? true);
    setSucursalEntrega(draft.sucursalEntrega || "");
    setObservacion(draft.observacion || "");
  }, [carritoStorageKey]);

  useEffect(() => {
    if (!carritoStorageKey) return;

    const isEmptyDraft =
      lineas.length === 0 &&
      fechaEntrega === getToday() &&
      horaDesde === "09:00" &&
      horaHasta === "18:00" &&
      retiraDelLocal &&
      !sucursalEntrega.trim() &&
      !observacion.trim();

    if (isEmptyDraft) {
      sessionStorage.removeItem(carritoStorageKey);
      return;
    }

    const draft = {
      lineas: sanitizeLineasForStorage(lineas),
      fechaEntrega,
      horaDesde,
      horaHasta,
      retiraDelLocal,
      sucursalEntrega,
      observacion,
    };

    sessionStorage.setItem(carritoStorageKey, JSON.stringify(draft));
  }, [carritoStorageKey, fechaEntrega, horaDesde, horaHasta, lineas, observacion, retiraDelLocal, sucursalEntrega]);

  const productosFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    return productos.filter((producto) => {
      const rubroOk = !idRubroSeleccionado || getProductoIdRubro(producto) === idRubroSeleccionado;
      const busquedaOk = !term || `${getProductoCodigo(producto)} ${getProductoNombre(producto)}`.toLowerCase().includes(term);
      return rubroOk && busquedaOk;
    });
  }, [productos, busqueda, idRubroSeleccionado]);

  const rubros = useMemo(() => {
    const map = new Map<number, { id: number; nombre: string; cantidad: number }>();
    productos.forEach((producto) => {
      const id = getProductoIdRubro(producto);
      const nombre = getProductoRubro(producto);
      if (!id || !nombre) return;
      const actual = map.get(id);
      map.set(id, { id, nombre, cantidad: (actual?.cantidad ?? 0) + 1 });
    });
    return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos]);

  const totalItems = useMemo(() => lineas.reduce((acc, linea) => acc + linea.cantidad, 0), [lineas]);
  const total = useMemo(() => lineas.reduce((acc, linea) => acc + getProductoPrecio(linea.producto) * linea.cantidad, 0), [lineas]);

  const cantidadEnCarritoPorProducto = useMemo(() => {
    const map = new Map<string, number>();
    lineas.forEach((linea) => {
      getProductoKeys(linea.producto).forEach((key) => {
        map.set(key, (map.get(key) ?? 0) + linea.cantidad);
      });
    });
    return map;
  }, [lineas]);

  const getCantidadEnCarrito = useCallback((producto: Producto) =>
    getProductoKeys(producto).reduce((cantidad, key) => Math.max(cantidad, cantidadEnCarritoPorProducto.get(key) ?? 0), 0),
    [cantidadEnCarritoPorProducto]);

  const agregarProducto = (producto: Producto) => {
    const productoNormalizado = normalizeProductoCarrito(producto);
    const productoKey = getProductoKey(productoNormalizado);
    setLineas((actuales) => {
      const existente = actuales.find((linea) => getProductoKey(linea.producto) === productoKey);
      if (existente) {
        return actuales.map((linea) => getProductoKey(linea.producto) === productoKey ? { ...linea, cantidad: linea.cantidad + 1 } : linea);
      }
      return [...actuales, { producto: productoNormalizado, cantidad: 1 }];
    });
  };

  const cambiarCantidad = (productoKey: string, cantidad: number) => {
    if (cantidad <= 0) {
      setLineas((actuales) => actuales.filter((linea) => getProductoKey(linea.producto) !== productoKey));
      return;
    }
    setLineas((actuales) => actuales.map((linea) => getProductoKey(linea.producto) === productoKey ? { ...linea, cantidad } : linea));
  };

  const guardarPedido = () => {
    if (!userLoggedIn || !idLocal || Number.isNaN(idLocal)) {
      showNotification("No hay un local activo configurado para pedidos web", "error");
      return;
    }
    if (!idCliente) {
      showNotification("El usuario no tiene un cliente asociado para emitir pedidos", "error");
      return;
    }
    if (lineas.length === 0) {
      showNotification("Agregue productos al carrito", "warning");
      return;
    }
    if (fechaEntrega < getToday()) {
      showNotification("La fecha de entrega no puede ser anterior a hoy", "warning");
      return;
    }
    if (!retiraDelLocal && !sucursalEntrega.trim()) {
      showNotification("Indique la sucursal o domicilio de entrega", "warning");
      return;
    }

    setGuardando(true);
    fetchCrearPedido(userLoggedIn, {
      idLocal,
      idCliente,
      cliente,
      fechaEntrega,
      horaDesde,
      horaHasta,
      observacion,
      retiraDelLocal,
      sucursalEntrega,
      origen: "WEB_CLIENTE",
    }, lineas)
      .then((res) => {
        const idPedido = res?.idPedido ?? res?.IdPedido;
        showNotification(idPedido ? `Pedido ${idPedido} generado` : "Pedido generado", "success");
        setLineas([]);
        sessionStorage.removeItem(carritoStorageKey);
        setCartOpen(false);
        setObservacion("");
      })
      .catch((err) => {
        console.log(err);
        showNotification(err?.message ?? "No se pudo generar el pedido", "error");
      })
      .finally(() => setGuardando(false));
  };

  const renderCarrito = (
    <Box sx={{ width: { xs: "100vw", sm: 440 }, minHeight: "100%", bgcolor: "background.default", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2.25, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>Carrito</Typography>
            <Typography variant="body2" color="text.secondary">
              {totalItems === 1 ? "1 producto" : `${totalItems} productos`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6" fontWeight={800}>{CurrencyFormatter.format(total)}</Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
      <Stack spacing={1.25}>
        {lineas.length === 0 ? (
          <Box sx={{ py: 5, px: 2, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
            <ShoppingCartIcon sx={{ color: "text.disabled", fontSize: 42, mb: 1 }} />
            <Typography fontWeight={700}>No hay productos agregados.</Typography>
            <Typography variant="body2" color="text.secondary">Elegí productos del catálogo para armar el pedido.</Typography>
          </Box>
        ) : lineas.map((linea) => {
          const productoKey = getProductoKey(linea.producto);
          const imagen = imagenesProductos[productoKey] || getProductoImagenLocal(linea.producto);
          const nombreProducto = getProductoNombre(linea.producto);
          return (
            <Box key={productoKey} sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: 1.25, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
              <Avatar src={imagen || undefined} variant="rounded" sx={{ width: 48, height: 48, bgcolor: "grey.100", color: "text.secondary", fontWeight: 700 }}>
                {nombreProducto.slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Tooltip title={nombreProducto} placement="top" arrow enterDelay={400}>
                  <Typography fontWeight={750} noWrap sx={{ cursor: "default" }}>{nombreProducto}</Typography>
                </Tooltip>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 0.25 }}>
                  <Typography variant="body2" color="text.secondary">{CurrencyFormatter.format(getProductoPrecio(linea.producto))}</Typography>
                  <Typography variant="body2" fontWeight={700}>{CurrencyFormatter.format(getProductoPrecio(linea.producto) * linea.cantidad)}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
                  <IconButton size="small" onClick={() => cambiarCantidad(productoKey, linea.cantidad - 1)} sx={{ border: "1px solid", borderColor: "divider" }}><RemoveIcon fontSize="small" /></IconButton>
                  <TextField size="small" type="number" value={linea.cantidad} inputProps={{ min: 0, step: "1", style: { textAlign: "center" } }} onChange={(e) => cambiarCantidad(productoKey, Math.max(parseDecimalValue(e.target.value), 0))} sx={{ width: 72, "& input": { py: 0.75 } }} />
                  <IconButton size="small" color="error" onClick={() => cambiarCantidad(productoKey, 0)} sx={{ border: "1px solid", borderColor: "divider" }}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Stack spacing={2} sx={{ mt: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
        <TextField type="date" label="Fecha de entrega" value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: getToday() }} fullWidth />
        <Stack direction="row" spacing={1}>
          <TextField type="time" label="Desde" value={horaDesde} onChange={(e) => setHoraDesde(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="time" label="Hasta" value={horaHasta} onChange={(e) => setHoraHasta(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
        <FormControlLabel control={<Checkbox checked={retiraDelLocal} onChange={(e) => setRetiraDelLocal(e.target.checked)} />} label="Retira del local" />
        {!retiraDelLocal && <TextField label="Sucursal o domicilio" value={sucursalEntrega} onChange={(e) => setSucursalEntrega(e.target.value)} fullWidth />}
        <TextField label="Observacion" value={observacion} onChange={(e) => setObservacion(e.target.value)} multiline minRows={2} fullWidth />
      </Stack>
      </Box>

      <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper", boxShadow: "0 -8px 20px rgba(0,0,0,0.04)" }}>
        <Button fullWidth size="large" variant="contained" startIcon={guardando ? <CircularProgress color="inherit" size={18} /> : <ShoppingCartIcon />} onClick={guardarPedido} disabled={guardando || lineas.length === 0}>
          {guardando ? "Enviando..." : "Enviar pedido"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: 10 }}>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h4">Comprar</Typography>
          <Typography color="text.secondary">{cliente}</Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Buscar productos"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
        </Grid>
        {rubros.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5, scrollbarWidth: "thin" }}>
              <Chip
                label={`Todos (${productos.length})`}
                clickable
                color={idRubroSeleccionado === 0 ? "primary" : "default"}
                variant={idRubroSeleccionado === 0 ? "filled" : "outlined"}
                onClick={() => setIdRubroSeleccionado(0)}
                sx={{ flex: "0 0 auto" }}
              />
              {rubros.map((rubro) => (
                <Chip
                  key={rubro.id}
                  label={`${rubro.nombre} (${rubro.cantidad})`}
                  clickable
                  color={idRubroSeleccionado === rubro.id ? "primary" : "default"}
                  variant={idRubroSeleccionado === rubro.id ? "filled" : "outlined"}
                  onClick={() => setIdRubroSeleccionado(rubro.id)}
                  sx={{ flex: "0 0 auto" }}
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>

      {loadingProductos ? (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {productosFiltrados.map((producto) => {
            const nombre = getProductoNombre(producto);
            const productoKey = getProductoKey(producto);
            const cantidadEnCarrito = getCantidadEnCarrito(producto);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={productoKey}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: 2 }}>
                  <ProductoImagenCatalogo producto={producto} userLoggedIn={userLoggedIn} onLoaded={registrarImagenProducto} />
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">{getProductoCodigo(producto)}</Typography>
                    <Typography fontWeight={700} sx={{ minHeight: 44 }}>{nombre}</Typography>
                    {getProductoRubro(producto) && <Typography variant="caption" color="text.secondary">{getProductoRubro(producto)}</Typography>}
                    <Typography variant="h6" sx={{ mt: 1 }}>{CurrencyFormatter.format(getProductoPrecio(producto))}</Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Badge badgeContent={cantidadEnCarrito} color="secondary" sx={{ width: "100%" }}>
                      <Button fullWidth variant="contained" startIcon={<AddShoppingCartIcon />} onClick={() => agregarProducto(producto)}>
                        {cantidadEnCarrito > 0 ? `Agregar (${cantidadEnCarrito})` : "Agregar"}
                      </Button>
                    </Badge>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Drawer anchor="right" open={cartOpen} onClose={() => setCartOpen(false)}>
        {renderCarrito}
      </Drawer>

      <Badge
        badgeContent={totalItems}
        color="primary"
        sx={{
          position: "fixed",
          right: { xs: 18, sm: 28 },
          bottom: { xs: 18, sm: 28 },
          zIndex: (theme) => theme.zIndex.drawer - 1,
        }}
      >
        <Fab color="primary" aria-label="Abrir carrito" onClick={() => setCartOpen(true)}>
          <ShoppingCartIcon />
        </Fab>
      </Badge>
    </Box>
  );
};

export default PedidoClienteCarrito;
